import { Box, Button, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
    useGetUserPreferencesQuery,
    useUpdateUserPreferencesMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../shared/AuthRoles';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from '../../shared/Toast';
import ChatNotifications from './ChatNotifications';
import ReservationNotifications from './ReservationNotifications';
import WeeklyDigestEmails from './WeeklyDigestEmails';
import ExperienceNotifications from './ExperienceNotifications';

const Schema = z.object({
    newMessageEmailNotification: z.boolean(),
    newMessagePushNotification: z.boolean(),
    reminderEmailNotification: z.boolean(),
    reservationEmailNotification: z.boolean(),
    reservationPushNotification: z.boolean(),
    reservationAppNotification: z.boolean(),
    reviewRequestEmailNotification: z.boolean(),
    weeklyDigestEmailNotification: z.boolean(),
    discountEmailNotification: z.boolean(),
    accessRequestEmailNotification: z.boolean(),
    accessRequestPushNotification: z.boolean(),
    accessRequestAppNotification: z.boolean(),
    reservationNotificationModules: z
        .array(z.number())
        .transform((arr) => arr?.map((value) => ({ id: value }))),
});

const NotificationSettings = () => {
    const loggedInUser = UserRole();

    const { data } = useGetUserPreferencesQuery(loggedInUser?.sub, {
        skip: !loggedInUser,
    });
    const [updatePreferences] = useUpdateUserPreferencesMutation();

    type Notifications = z.infer<typeof Schema>;

    const methods = useForm<Notifications>({
        defaultValues: {
            newMessageEmailNotification: false,
            newMessagePushNotification: false,
            reminderEmailNotification: false,
            reservationEmailNotification: false,
            reservationPushNotification: false,
            reservationAppNotification: false,
            reviewRequestEmailNotification: false,
            weeklyDigestEmailNotification: false,
            discountEmailNotification: false,
            accessRequestEmailNotification: false,
            accessRequestPushNotification: false,
            accessRequestAppNotification: false,
            reservationNotificationModules: [],
        },
        resolver: zodResolver(Schema),
    });

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty },
    } = methods;

    const onSubmit = async (data: Notifications) => {
        try {
            const preferences = await updatePreferences(data).unwrap();
            reset({
                ...preferences,
                // @ts-expect-error
                reservationNotificationModules:
                    data.reservationNotificationModules?.map(({ id }) => id),
            });

            toast.success('Notification settings updated successfully');
        } catch (error) {
            toast.error('Error updating notification settings');
        }
    };

    const handleCancel = () => {
        reset(data);
    };

    useEffect(() => {
        if (data)
            reset({
                ...data,
                // @ts-expect-error
                reservationNotificationModules:
                    data.reservationNotificationModules?.map(({ id }) => id),
            });
    }, [data]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack gap={4} pt='30px'>
                    <ReservationNotifications />
                    {loggedInUser?.role?.includes('owner') && (
                        <WeeklyDigestEmails />
                    )}
                    {loggedInUser?.role !== 'external_attendee_user' && (
                        <ChatNotifications />
                    )}
                    {loggedInUser?.role?.includes('guest') && (
                        <ExperienceNotifications />
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            pb: { md: 12 },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                            }}
                        >
                            {isDirty && (
                                <Typography
                                    variant='caption'
                                    sx={{ color: 'text.primary' }}
                                >
                                    You have unsaved changes
                                </Typography>
                            )}
                            <Button
                                variant='outlined'
                                disabled={!isDirty}
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button variant='contained' type='submit'>
                                Save
                            </Button>
                        </Box>
                    </Box>
                </Stack>
            </form>
        </FormProvider>
    );
};

export default NotificationSettings;
